import { useCallback, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  SettingsType,
  applyPipeexSettingsToConfigurationStore,
  getPipeexSettings,
  setActivePipeexProfile,
  summarizePcfSettingsDtoDataFlowForDebug,
  summarizePcfSettingsDtoForDebug,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import type { MappingConfiguration } from '../../../shared/types/config/mapping.types';
import { parseSettingsType } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import {
  debugMappingProfile,
  elapsedMs,
  errorMessage,
  nowMs,
  startProfileStallWarning,
  type MappingProfileApplyProgressState,
} from './profileControllerShared';

interface ConfirmedProfileRef {
  name: string;
  settingsType: SettingsType;
  profileKey: string;
}

interface UseProfileLoadActionsArgs {
  selectedProfileKey: string;
  selectedProfileKeyRef: MutableRefObject<string>;
  setSelectedProfileKey: Dispatch<SetStateAction<string>>;
  setSelectedSettingsType: Dispatch<SetStateAction<SettingsType>>;
  setLastAppliedProfileKey: Dispatch<SetStateAction<string>>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  setProfileError: Dispatch<SetStateAction<string | null>>;
  setGeneralSettings: Dispatch<SetStateAction<MappingConfiguration['general']>>;
  setAdvancedSettings: Dispatch<SetStateAction<MappingConfiguration['advanced']>>;
  loadProfiles: () => Promise<void>;
  onProfileConfirmed?: (profile: ConfirmedProfileRef) => void;
}

/**
 * "Load this profile into the wizard stores" action plus the apply-progress
 * window model that tracks its phases for the progress dialog.
 */
export const useProfileLoadActions = ({
  selectedProfileKey,
  selectedProfileKeyRef,
  setSelectedProfileKey,
  setSelectedSettingsType,
  setLastAppliedProfileKey,
  setIsSaving,
  setSaveError,
  setProfileError,
  setGeneralSettings,
  setAdvancedSettings,
  loadProfiles,
  onProfileConfirmed,
}: UseProfileLoadActionsArgs) => {
  const [profileApplyProgress, setProfileApplyProgress] =
    useState<MappingProfileApplyProgressState | null>(null);

  const clearProfileApplyProgress = useCallback(() => {
    setProfileApplyProgress((current) =>
      current?.phase === 'loadProfile' ||
      current?.phase === 'applyProfile' ||
      current?.phase === 'refreshProfiles'
        ? current
        : null
    );
  }, []);

  const onLoadProfile = useCallback(async (profileKeyOverride?: string): Promise<boolean> => {
    const profileKeyToLoad =
      typeof profileKeyOverride === 'string' && profileKeyOverride.trim()
        ? profileKeyOverride
        : selectedProfileKey;

    if (!profileKeyToLoad) {
      return false;
    }

    const [name, settingsTypeRaw] = profileKeyToLoad.split('::');
    const settingsType = parseSettingsType(settingsTypeRaw);
    const startedAt = nowMs();
    const stallTimer = startProfileStallWarning(
      'profile load still pending',
      {
        profileKey: profileKeyToLoad,
        profileKeyOverride: profileKeyOverride ?? null,
        settingsType,
      },
      startedAt
    );
    debugMappingProfile('[MappingProfileController] load selected profile start', {
      profileKey: profileKeyToLoad,
      profileKeyOverride: profileKeyOverride ?? null,
      settingsType,
    });
    selectedProfileKeyRef.current = profileKeyToLoad;
    setSelectedProfileKey(profileKeyToLoad);
    setSelectedSettingsType(settingsType);
    setProfileApplyProgress({
      profileKey: profileKeyToLoad,
      profileName: name,
      settingsType,
      phase: 'loadProfile',
      message: `Reading “${name}” mapping profile`,
      detail: 'PipeEX is reading the saved profile before replacing the current workspace settings.',
      startedAt,
    });

    setIsSaving(true);
    setSaveError(null);
    setProfileError(null);
    try {
      const dto = await getPipeexSettings(name, settingsType);
      setProfileApplyProgress((current) =>
        current?.profileKey === profileKeyToLoad
          ? {
              ...current,
              phase: 'applyProfile',
              message: `Applying “${name}” to the PipeEX workspace`,
              detail: 'Replacing the current Mapping, Quick Fill, PCF Validation, and PCF export settings with values from the selected profile.',
              elapsedMs: elapsedMs(startedAt),
            }
          : current
      );
      debugMappingProfile('[MappingProfileController] load selected profile fetched dto', {
        profileKey: profileKeyToLoad,
        elapsedMs: elapsedMs(startedAt),
        summary: summarizePcfSettingsDtoForDebug(dto),
        dataFlow: summarizePcfSettingsDtoDataFlowForDebug(dto),
      });
      applyPipeexSettingsToConfigurationStore(dto, {
        restoreWizardProgress: false,
      });
      setActivePipeexProfile(name, settingsType);

      const latest = useConfigurationStore.getState().mappingConfiguration;
      setGeneralSettings(latest.general);
      setAdvancedSettings(latest.advanced);
      setLastAppliedProfileKey(profileKeyToLoad);
      onProfileConfirmed?.({
        name,
        settingsType,
        profileKey: profileKeyToLoad,
      });
      setProfileApplyProgress((current) =>
        current?.profileKey === profileKeyToLoad
          ? {
              ...current,
              phase: 'refreshProfiles',
              message: `Confirming “${name}” as the active profile`,
              detail: 'Refreshing the profile list before PipeEX compares the selected profile with the active Revit project.',
              elapsedMs: elapsedMs(startedAt),
            }
          : current
      );
      await loadProfiles();
      debugMappingProfile('[MappingProfileController] load selected profile complete', {
        profileKey: profileKeyToLoad,
        elapsedMs: elapsedMs(startedAt),
      });
      setProfileApplyProgress((current) =>
        current?.profileKey === profileKeyToLoad
          ? {
              ...current,
              phase: 'complete',
              message: `“${name}” is active; checking the Revit project`,
              detail: 'The profile settings are applied. PipeEX is now checking for missing project and family parameters before handoff.',
              elapsedMs: elapsedMs(startedAt),
            }
          : current
      );
      return true;
    } catch (error) {
      const message = error instanceof Error
        ? `PipeEX could not apply “${name}”: ${error.message}. The profile handoff is incomplete; refresh profiles, then try again.`
        : `PipeEX could not apply “${name}” because the service returned no error details. The profile handoff is incomplete; refresh profiles, then try again.`;
      warnMappingFlow('profile load failed', {
        profileKey: profileKeyToLoad,
        elapsedMs: elapsedMs(startedAt),
        error: errorMessage(error),
      });
      setProfileError(message);
      setProfileApplyProgress((current) =>
        !current || current.profileKey === profileKeyToLoad
          ? {
              profileKey: profileKeyToLoad,
              profileName: name,
              settingsType,
              phase: 'error',
              message: `PipeEX could not apply “${name}”`,
              detail: 'The selected profile did not reach project inspection. Review the cause below, refresh profiles, and retry.',
              error: message,
              startedAt,
              elapsedMs: elapsedMs(startedAt),
            }
          : current
      );
      return false;
    } finally {
      globalThis.clearTimeout(stallTimer);
      setIsSaving(false);
    }
  }, [loadProfiles, onProfileConfirmed, selectedProfileKey, setAdvancedSettings, setGeneralSettings]);

  return {
    profileApplyProgress,
    clearProfileApplyProgress,
    onLoadProfile,
  };
};
